export const kpiMinDecimalPlaces = 2;
export const kpiMaxDecimalPlaces = 15;

export function normalizeKpiDecimalPlaces(
    decimalPlaces: number | null | undefined
): number {
    if (decimalPlaces == null || !Number.isFinite(decimalPlaces)) {
        return kpiMinDecimalPlaces;
    }

    return Math.min(
        kpiMaxDecimalPlaces,
        Math.max(kpiMinDecimalPlaces, Math.trunc(decimalPlaces))
    );
}

export function formatKpiFixedNumber(
    value: number,
    decimalPlaces: number | null | undefined,
    options: {
        thousandSeparator?: boolean;
        trimTrailingZeros?: boolean;
        locale?: string;
    } = {}
): string {
    const requested = normalizeKpiDecimalPlaces(decimalPlaces);
    let formatted = formatKpiDecimalSafeFixed(value, requested);

    if (options.trimTrailingZeros) {
        formatted = trimKpiFixedTrailingZeros(formatted);
    }

    if (/[eE]/.test(formatted)) {
        return formatted;
    }

    if (!options.thousandSeparator && !options.locale) {
        return formatted;
    }

    const symbols = localeNumberSymbols(options.locale);
    const [integerPart, fractionPart] = formatted.split('.');
    const sign = integerPart.startsWith('-') ? '-' : '';
    const digits = sign ? integerPart.slice(1) : integerPart;
    const grouped = options.thousandSeparator
        ? digits.replace(/\B(?=(\d{3})+(?!\d))/g, symbols.group)
        : digits;

    return fractionPart === undefined
        ? `${sign}${grouped}`
        : `${sign}${grouped}${symbols.decimal}${fractionPart}`;
}

function maxKpiFractionDigitsForMagnitude(value: number): number {
    if (!Number.isFinite(value)) {
        return kpiMaxDecimalPlaces;
    }

    const abs = Math.abs(value);
    if (abs === 0 || abs < 1) {
        return kpiMaxDecimalPlaces;
    }

    return Math.max(0, kpiMaxDecimalPlaces - integerDigitsForMagnitude(abs));
}

/** Integer-digit count for absolute values of at least 1. */
function integerDigitsForMagnitude(abs: number): number {
    const digits = Math.floor(Math.log10(abs)) + 1;
    if (digits > 1 && abs < Math.pow(10, digits - 1)) {
        return digits - 1;
    }
    if (abs >= Math.pow(10, digits)) {
        return digits + 1;
    }
    return digits;
}

function localeNumberSymbols(locale?: string): {
    group: string;
    decimal: string;
} {
    if (!locale) {
        return { group: ',', decimal: '.' };
    }

    const parts = new Intl.NumberFormat(locale).formatToParts(12345.67);
    return {
        group: parts.find(part => part.type === 'group')?.value ?? ',',
        decimal: parts.find(part => part.type === 'decimal')?.value ?? '.',
    };
}

/** Magnitude where JavaScript fixed formatting switches to exponential notation. */
const kpiExponentialNotationAbs = 1e21;

function formatKpiDecimalSafeFixed(value: number, requested: number): string {
    if (!Number.isFinite(value)) {
        return String(value);
    }

    const places = Math.min(requested, maxKpiFractionDigitsForMagnitude(value));
    const sign = value < 0 && value !== 0 ? '-' : '';
    const abs = Math.abs(value);

    // Shared with the backend helper: midpoint away from zero on the
    // binary double. Do not snap to 15 decimal digits (1.005 at 2
    // places is 1.00) and do not scale then round, which rounds
    // inexact values such as 1.15 incorrectly. Keep JavaScript
    // exponential at 1 times 10 to the 21 or above; skip padding
    // there.
    if (abs >= kpiExponentialNotationAbs) {
        return sign + abs.toString();
    }

    const core = abs.toFixed(places);
    if (places >= requested) {
        return sign + core;
    }

    const padded = core.includes('.')
        ? core + '0'.repeat(requested - places)
        : `${core}.${'0'.repeat(requested)}`;

    return sign + padded;
}

function trimKpiFixedTrailingZeros(formatted: string): string {
    const exponentIndex = formatted.search(/[eE]/);
    if (exponentIndex === -1) {
        return formatted.replace(/\.?0+$/, '');
    }

    const mantissa = formatted.slice(0, exponentIndex);
    const exponent = formatted.slice(exponentIndex);
    return mantissa.replace(/\.?0+$/, '') + exponent;
}
